import React, { useState, useEffect, useRef } from 'react';
import { contentService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { 
  Plus, Edit2, Trash2, Eye, EyeOff, Search, 
  FileText, Clock, Heart, Calendar, X, Save,
  Image as ImageIcon, Upload, ArrowLeft, Sparkles,
  Wand2, FileSearch, Timer, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const CATEGORIES = [
  { value: 'ev_arac', label: 'Elektrikli Araçlar', color: 'bg-blue-500', icon: '🚗' },
  { value: 'sarj_teknoloji', label: 'Şarj Teknolojileri', color: 'bg-orange-500', icon: '⚡' },
  { value: 'gunes_enerjisi', label: 'Güneş Enerjisi', color: 'bg-yellow-500', icon: '☀️' },
  { value: 'enerji_depolama', label: 'Enerji Depolama', color: 'bg-green-500', icon: '🔋' },
  { value: 'genel', label: 'Genel', color: 'bg-purple-500', icon: '📰' },
];

// Content Editor Component (Full Page)
const ContentEditor = ({ content, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState({
    title: content?.title || '',
    summary: content?.summary || '',
    body: content?.body || '',
    cover_image: content?.cover_image || '',
    category: content?.category || 'genel',
    read_time: content?.read_time || 5,
    is_published: content?.is_published || false,
  });
  const [previewMode, setPreviewMode] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [aiLoading, setAiLoading] = useState({ draft: false, summary: false });
  const fileInputRef = useRef(null);

  // AI: Makale taslağı oluştur
  const handleGenerateDraft = async () => {
    if (!aiTopic.trim()) {
      toast.error('Lütfen bir konu girin');
      return;
    }
    
    setAiLoading(prev => ({ ...prev, draft: true }));
    try {
      const response = await contentService.generateDraft(aiTopic);
      if (response.success && response.draft) {
        setFormData(prev => ({ ...prev, body: response.draft }));
        toast.success('Taslak oluşturuldu!');
      } else {
        throw new Error('AI yanıt vermedi');
      }
    } catch (error) {
      toast.error('Taslak oluşturulamadı');
    } finally {
      setAiLoading(prev => ({ ...prev, draft: false }));
    }
  };

  // AI: Özet oluştur
  const handleGenerateSummary = async () => {
    if (!formData.body.trim() || formData.body === '<p><br></p>') {
      toast.error('Önce içerik yazın');
      return;
    }
    
    setAiLoading(prev => ({ ...prev, summary: true }));
    try {
      const response = await contentService.generateSummary(formData.body);
      if (response.success && response.summary) {
        setFormData(prev => ({ ...prev, summary: response.summary }));
        toast.success('Özet oluşturuldu!');
      } else {
        throw new Error('AI yanıt vermedi');
      }
    } catch (error) {
      toast.error('Özet oluşturulamadı');
    } finally {
      setAiLoading(prev => ({ ...prev, summary: false }));
    }
  };

  // Okuma süresi hesapla
  const calculateReadTime = () => {
    const text = formData.body.replace(/<[^>]*>/g, '');
    const wordCount = text.split(/\s+/).filter(w => w.length > 0).length;
    const readTime = Math.max(1, Math.ceil(wordCount / 200));
    setFormData(prev => ({ ...prev, read_time: readTime }));
    toast.success(`Okuma süresi: ${readTime} dakika (${wordCount} kelime)`);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`Dosya çok büyük (${(file.size / 1024 / 1024).toFixed(1)}MB). Maksimum 10MB yüklenebilir.`);
      e.target.value = '';
      return;
    }

    setImageUploading(true);
    try {
      // Upload file to server
      const token = localStorage.getItem('hims_token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });
      
      if (response.ok) {
        const data = await response.json();
        // Store the URL path (e.g., /uploads/abc123.jpg)
        setFormData(prev => ({ ...prev, cover_image: data.url }));
        toast.success('Görsel yüklendi');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Görsel yüklenirken hata oluştu');
      }
    } catch (error) {
      toast.error('Görsel yüklenirken hata oluştu');
    } finally {
      setImageUploading(false);
    }
  };

  const handleSubmit = () => {
    if (!formData.title.trim()) {
      toast.error('Başlık zorunludur');
      return;
    }
    if (!formData.summary.trim()) {
      toast.error('Özet zorunludur');
      return;
    }
    if (!formData.body.trim() || formData.body === '<p><br></p>') {
      toast.error('İçerik zorunludur');
      return;
    }
    onSave(formData);
  };

  // Calculate read time based on content
  useEffect(() => {
    const wordCount = formData.body.replace(/<[^>]*>/g, '').split(/\s+/).length;
    const readTime = Math.max(1, Math.ceil(wordCount / 200)); // 200 words per minute
    setFormData(prev => ({ ...prev, read_time: readTime }));
  }, [formData.body]);

  return (
    <div className="fixed inset-0 bg-background z-50 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="border-b border-border px-6 py-4 flex items-center justify-between bg-card">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onCancel}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-lg font-semibold">
              {content ? 'İçerik Düzenle' : 'Yeni İçerik Oluştur'}
            </h1>
            <p className="text-sm text-muted-foreground">
              Profesyonel içerik editörü
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Preview Toggle */}
          <Button 
            variant={previewMode ? "default" : "outline"} 
            onClick={() => setPreviewMode(!previewMode)}
          >
            <Eye className="w-4 h-4 mr-2" />
            {previewMode ? 'Düzenleme' : 'Önizleme'}
          </Button>
          
          {/* Publish Toggle */}
          <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-muted">
            <input
              type="checkbox"
              id="publish-toggle"
              checked={formData.is_published}
              onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
              className="w-4 h-4 rounded"
            />
            <Label htmlFor="publish-toggle" className="text-sm cursor-pointer">
              {formData.is_published ? 'Yayında' : 'Taslak'}
            </Label>
          </div>
          
          {/* Save Button */}
          <Button onClick={handleSubmit} disabled={saving} className="min-w-[120px]">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden flex">
        {/* Editor Panel */}
        <div className={`flex-1 overflow-y-auto p-6 ${previewMode ? 'hidden' : ''}`}>
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Cover Image Section */}
            <div className="space-y-3">
              <Label className="text-base font-medium">Kapak Görseli</Label>
              <div 
                className={`relative border-2 border-dashed border-border rounded-xl overflow-hidden transition-all hover:border-primary/50 ${
                  formData.cover_image ? 'h-[300px]' : 'h-[200px]'
                }`}
              >
                {formData.cover_image ? (
                  <>
                    <img 
                      src={formData.cover_image} 
                      alt="Cover" 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                      <Button 
                        variant="secondary" 
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Değiştir
                      </Button>
                      <Button 
                        variant="destructive" 
                        onClick={() => setFormData({ ...formData, cover_image: '' })}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Kaldır
                      </Button>
                    </div>
                  </>
                ) : (
                  <div 
                    className="w-full h-full flex flex-col items-center justify-center cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {imageUploading ? (
                      <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary"></div>
                    ) : (
                      <>
                        <ImageIcon className="w-12 h-12 text-muted-foreground mb-3" />
                        <p className="text-muted-foreground">Görsel yüklemek için tıklayın veya sürükleyin</p>
                        <p className="text-xs text-muted-foreground mt-1">PNG, JPG, WEBP - Max 5MB</p>
                      </>
                    )}
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>
              
              {/* Or paste URL */}
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">veya</span>
                <Input
                  placeholder="Görsel URL'si yapıştırın..."
                  value={formData.cover_image?.startsWith('data:') ? '' : formData.cover_image}
                  onChange={(e) => setFormData({ ...formData, cover_image: e.target.value })}
                  className="flex-1"
                />
              </div>
            </div>

            {/* Category & Read Time */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-base font-medium">Kategori</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        <span className="flex items-center gap-2">
                          <span>{cat.icon}</span>
                          {cat.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-base font-medium">Okuma Süresi</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={1}
                    max={60}
                    value={formData.read_time}
                    onChange={(e) => setFormData({ ...formData, read_time: parseInt(e.target.value) || 5 })}
                    className="h-12"
                  />
                  <span className="text-muted-foreground">dakika</span>
                </div>
              </div>
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label className="text-base font-medium">Başlık *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Dikkat çekici bir başlık yazın..."
                className="h-14 text-xl font-semibold"
              />
            </div>

            {/* Summary */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">Özet *</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleGenerateSummary}
                  disabled={aiLoading.summary || !formData.body.trim()}
                  className="gap-2"
                >
                  {aiLoading.summary ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <FileSearch className="w-4 h-4" />
                  )}
                  Özet Çıkar
                </Button>
              </div>
              <textarea
                value={formData.summary}
                onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                placeholder="Haberin kısa özeti (kart görünümünde gösterilir)..."
                className="w-full h-24 px-4 py-3 rounded-lg border border-input bg-background text-base resize-none focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            {/* AI Draft Generator */}
            <div className="p-4 rounded-xl bg-gradient-to-r from-primary/5 to-purple-500/5 border border-primary/20">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-primary" />
                <Label className="text-base font-medium">AI Yardımcısı</Label>
              </div>
              <div className="flex gap-2">
                <Input
                  value={aiTopic}
                  onChange={(e) => setAiTopic(e.target.value)}
                  placeholder="Makale konusu yazın... Örn: Türkiye'de EV şarj altyapısı"
                  className="flex-1"
                />
                <Button
                  type="button"
                  onClick={handleGenerateDraft}
                  disabled={aiLoading.draft || !aiTopic.trim()}
                  className="gap-2 min-w-[160px]"
                >
                  {aiLoading.draft ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Wand2 className="w-4 h-4" />
                  )}
                  {aiLoading.draft ? 'Yazılıyor...' : 'AI ile Taslak Yaz'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                AI, EV teknolojileri hakkında profesyonel makale taslağı oluşturur
              </p>
            </div>

            {/* Rich Text Editor - Using Textarea for now */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">İçerik *</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={calculateReadTime}
                  className="gap-2"
                >
                  <Timer className="w-4 h-4" />
                  Okuma Süresi Hesapla
                </Button>
              </div>
              <div className="border border-input rounded-lg overflow-hidden bg-background">
                <div className="bg-muted px-4 py-2 border-b border-border flex items-center gap-2 text-sm text-muted-foreground">
                  <span>HTML desteklenir: &lt;p&gt;, &lt;h2&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;a href=""&gt;</span>
                </div>
                <textarea
                  value={formData.body}
                  onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                  placeholder="İçeriğinizi buraya yazın... HTML etiketleri kullanabilirsiniz."
                  className="w-full min-h-[400px] px-4 py-4 bg-background text-base resize-y focus:outline-none leading-relaxed"
                  style={{ fontFamily: 'inherit' }}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                💡 İpucu: HTML etiketleri kullanarak içeriğinizi zenginleştirebilirsiniz. Örn: &lt;p&gt;Paragraf&lt;/p&gt;, &lt;h2&gt;Başlık&lt;/h2&gt;
              </p>
            </div>
          </div>
        </div>

        {/* Preview Panel */}
        {previewMode && (
          <div className="flex-1 overflow-y-auto p-6 bg-muted/30">
            <div className="max-w-3xl mx-auto bg-card rounded-xl shadow-lg overflow-hidden">
              {/* Cover */}
              {formData.cover_image && (
                <div className="relative h-[300px]">
                  <img 
                    src={formData.cover_image} 
                    alt="Preview" 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <span className={`absolute bottom-4 left-4 px-3 py-1 rounded-full text-sm font-medium text-white ${CATEGORIES.find(c => c.value === formData.category)?.color || 'bg-purple-500'}`}>
                    {CATEGORIES.find(c => c.value === formData.category)?.label || 'Genel'}
                  </span>
                </div>
              )}
              
              <div className="p-8">
                <h1 className="text-3xl font-bold mb-4">{formData.title || 'Başlık'}</h1>
                <p className="text-lg text-muted-foreground mb-6">{formData.summary || 'Özet'}</p>
                
                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-8 pb-6 border-b">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {formData.read_time} dk okuma
                  </span>
                  <span className="flex items-center gap-1">
                    <Heart className="w-4 h-4" />
                    0
                  </span>
                </div>
                
                <div 
                  className="prose prose-lg dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: formData.body || '<p>İçerik buraya gelecek...</p>' }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Content Management Page
const ContentManagementPage = () => {
  const { user, isSuperOrAdmin } = useAuth();
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadContents();
  }, []);

  const loadContents = async () => {
    try {
      setLoading(true);
      const response = await contentService.list({ published_only: false, limit: 100 });
      setContents(response.items || []);
    } catch (error) {
      toast.error('İçerikler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditor = (content = null) => {
    setEditingContent(content);
    setShowEditor(true);
  };

  const handleCloseEditor = () => {
    setShowEditor(false);
    setEditingContent(null);
  };

  const handleSave = async (formData) => {
    try {
      setSaving(true);
      if (editingContent) {
        await contentService.update(editingContent.id, formData);
        toast.success('İçerik güncellendi');
      } else {
        await contentService.create(formData);
        toast.success('İçerik oluşturuldu');
      }
      handleCloseEditor();
      loadContents();
    } catch (error) {
      toast.error(error.message || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu içeriği silmek istediğinizden emin misiniz?')) return;
    
    try {
      await contentService.delete(id);
      toast.success('İçerik silindi');
      loadContents();
    } catch (error) {
      toast.error(error.message || 'Silme işlemi başarısız');
    }
  };

  const handleTogglePublish = async (content) => {
    try {
      await contentService.update(content.id, { is_published: !content.is_published });
      toast.success(content.is_published ? 'Taslağa alındı' : 'Yayınlandı');
      loadContents();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const filteredContents = contents.filter(content =>
    content.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    content.summary?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const getCategoryInfo = (categoryValue) => {
    return CATEGORIES.find(c => c.value === categoryValue) || CATEGORIES[4];
  };

  // Show Full Page Editor
  if (showEditor) {
    return (
      <ContentEditor 
        content={editingContent}
        onSave={handleSave}
        onCancel={handleCloseEditor}
        saving={saving}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="content-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <FileText className="w-7 h-7 text-primary" />
            İçerik Yönetimi
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Haber merkezi içeriklerini oluşturun ve yönetin
          </p>
        </div>
        
        <Button onClick={() => handleOpenEditor()} size="lg" className="gap-2">
          <Plus className="w-5 h-5" />
          Yeni İçerik Oluştur
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-2xl font-bold text-foreground">{contents.length}</div>
          <div className="text-sm text-muted-foreground">Toplam İçerik</div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-2xl font-bold text-green-500">{contents.filter(c => c.is_published).length}</div>
          <div className="text-sm text-muted-foreground">Yayında</div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-2xl font-bold text-amber-500">{contents.filter(c => !c.is_published).length}</div>
          <div className="text-sm text-muted-foreground">Taslak</div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-2xl font-bold text-primary">{contents.reduce((sum, c) => sum + (c.likes_count || 0), 0)}</div>
          <div className="text-sm text-muted-foreground">Toplam Beğeni</div>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Başlık veya özet ara..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Content Grid */}
      {filteredContents.length === 0 ? (
        <div className="text-center py-16 bg-card border border-border rounded-xl">
          <Sparkles className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold mb-2">Henüz içerik yok</h3>
          <p className="text-muted-foreground mb-6">İlk içeriğinizi oluşturmak için butona tıklayın</p>
          <Button onClick={() => handleOpenEditor()}>
            <Plus className="w-4 h-4 mr-2" />
            İlk İçeriği Oluştur
          </Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredContents.map((content) => {
            const categoryInfo = getCategoryInfo(content.category);
            return (
              <div 
                key={content.id}
                className="bg-card border border-border rounded-xl p-4 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start gap-4">
                  {/* Thumbnail */}
                  {content.cover_image ? (
                    <img 
                      src={content.cover_image} 
                      alt="" 
                      className="w-32 h-20 rounded-lg object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-32 h-20 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                      <ImageIcon className="w-8 h-8 text-muted-foreground" />
                    </div>
                  )}
                  
                  {/* Content Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-semibold text-foreground line-clamp-1">{content.title}</h3>
                        <p className="text-sm text-muted-foreground line-clamp-1 mt-1">{content.summary}</p>
                      </div>
                      
                      {/* Status Badge */}
                      <span className={`flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                        content.is_published 
                          ? 'bg-green-500/10 text-green-500' 
                          : 'bg-amber-500/10 text-amber-500'
                      }`}>
                        {content.is_published ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                        {content.is_published ? 'Yayında' : 'Taslak'}
                      </span>
                    </div>
                    
                    {/* Meta */}
                    <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium text-white ${categoryInfo.color}`}>
                        {categoryInfo.icon} {categoryInfo.label}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {content.read_time} dk
                      </span>
                      <span className="flex items-center gap-1">
                        <Heart className="w-3.5 h-3.5" />
                        {content.likes_count || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDate(content.created_at)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleTogglePublish(content)}
                      title={content.is_published ? 'Taslağa Al' : 'Yayınla'}
                    >
                      {content.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleOpenEditor(content)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    {isSuperOrAdmin() && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(content.id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ContentManagementPage;
